import Button from '@ingka/button';
import '@ingka/button/dist/style.css';
import '@ingka/focus/dist/style.css';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import '@ingka/modal/dist/style.css';
import '@ingka/svg-icon/dist/style.css';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { useAppDispatch } from 'ducks/hooks';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Body from 'ui/Typography/Body';
import Title, { HeadingType } from 'ui/Typography/Title';
import { hideBillyCornerStabilitySheet, isModalVisible } from './BillyCornerStabilityFeature';
import Box from 'ui/Box';
import StabilityImage from './billy_corner_stability_img.png';
import { CoreApi, ProductType } from '@inter-ikea-storageone/range';
import { ComponentIds, GuestConnectionsComponent, HierarchyUtils } from '@inter-ikea-core/core';
import { MeasurementUtility } from '@inter-ikea-kompis/utilities';
import { useTranslations } from 'hooks/useTranslations';
import { useServiceSettings } from 'contexts/serviceSettings';

const Image = styled.img`
  width: 100%;
`;

export const shouldShowCornerStabilitySheet = (coreApi: CoreApi) => {
  const cornerFrames = coreApi.entities
    .getByProductType(ProductType.BILLY_FRAME_WITH_SHELVES_ASM)
    .filter(
      frame =>
        frame.getComponent<GuestConnectionsComponent>(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)
          ?.highestPriorityConnection?.guestConnector.key === 'wall-corner'
    );
  const unstableCornerFrames = cornerFrames.filter(frameAssembly => {
    const frame = HierarchyUtils.children(frameAssembly)[0];
    const alignedFrames = coreApi.entities.getAlignedFrames(
      frame,
      'align-left',
      'align-right',
      true,
      true
    );
    return alignedFrames.left === null || alignedFrames.right === null;
  });
  return unstableCornerFrames.length > 0;
};

const BillyCornerStabilitySheet = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { unit, useMetric } = useTranslations();
  const { translations } = useServiceSettings();

  const narrow = useMetric ? 40 : MeasurementUtility.metricToImperial(translations.kompis, 400);
  const wide = useMetric ? 80 : MeasurementUtility.metricToImperial(translations.kompis, 800);
  const height = useMetric ? 202 : MeasurementUtility.metricToImperial(translations.kompis, 202);

  const modalVisible = useSelector(isModalVisible);

  const onDismiss = () => {
    dispatch(hideBillyCornerStabilitySheet());
  };

  return (
    <Modal
      data-testid="billy-corner-stability-sheet"
      visible={!!modalVisible}
      escapable={true}
      handleCloseBtn={onDismiss}
    >
      <Sheets
        title={formatMessage({ id: 'storageone.ivarStabilityInlineMessageTitle' })}
        header={
          <ModalHeader
            data-testid="modal-header"
            ariaCloseTxt={formatMessage({ id: 'storageone.closeButton' })}
          />
        }
        footer={
          <ModalFooter>
            <Button
              text={formatMessage({ id: 'storageone.nightLightsLightSourceLimitsSheetButton' })}
              onClick={onDismiss}
              data-testid="billy-corner-stability-sheet-ok-btn"
              type="primary"
              fluid
            />
          </ModalFooter>
        }
      >
        <ModalBody>
          <Title
            mb="l"
            type={ThemeFontStyleTypeEnum.headingMedium}
            heading={HeadingType.HeadingTwo}
          >
            {formatMessage({ id: 'storageone.ivarStabilityInlineMessageTitle' })}
          </Title>
          <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
            {formatMessage({ id: 'storageone.stabilitySheetDescriptionBilly' })}
          </Body>
          <Box px={'xxxxxl'} py={'xl'} style={{ width: '100%' }}>
            <Image
              src={StabilityImage}
              alt={formatMessage(
                { id: 'storageone.stabilitySheetDescriptionBilly' },
                { height, unit }
              )}
            />
          </Box>
          <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
            {formatMessage(
              { id: 'storageone.stabilitySheetAdditionalDescriptionBilly' },
              { narrow, wide, unit }
            )}
          </Body>
        </ModalBody>
      </Sheets>
    </Modal>
  );
};

export default BillyCornerStabilitySheet;
